<template>
  <div id="pdp-main-container" class="pdp-main-section">
    <loader
      v-if="forceRedirectOnClientSide"
      class="my-5"
    />
    <div v-else>
      <DealBannerHeader
        :banner-details="deal.banner_details"
        :display-timer="displayTimer"
        class="d-flex d-lg-none mb-1 pl-2"
        :class="showBannerDetails ? 'd-flex d-lg-none' : 'd-none'"
      />
      <div class="container pdp-section">
        <SmartCollectionZone
          v-if="useSmartCollectionZones"
          zone="pdp_top"
          :template-slug="deal.slug"
          :relative-to-container="true"
          :conditions="zoneConditions"
          :preloaded-data="zoneData.pdp_top"
        />

        <PDPDealStory
          :deal="deal"
          :user="user"
          :show-banner-details="showBannerDetails"
          :display-timer="displayTimer"
          :show-breadcrumbs="showBreadcrumbs"
          :breadcrumb-objects="breadcrumbObjects"
        />
      </div>
      <PDPSectionTabs
        v-if="!hideStickyTabs"
        enable-sticky
        :deal="deal"
        :has-founders-post="hasFoundersPost"
        :currently-before-next-deal-stage="currentlyBeforeNextDealStage"
        :display-timer="displayTimer"
      />
      <div
        v-if="!deal.is_info && inactiveIndefinitely"
        class="container pdp-section"
      >
        <SmartCollectionZone
          v-if="useSmartCollectionZones"
          zone="pdp_before_terms"
          :template-slug="deal.slug"
          :relative-to-container="true"
          :conditions="zoneConditions"
          :preloaded-data="zoneData.pdp_before_terms"
        />
        <!-- show Plans & Features section (DealTermsAndFeatures + PricingTable) for sold out -->
        <DealTermsAndFeatures
          v-if="isSoldOut"
          class="mt-5"
          :deal="deal"
        />
        <PricingTable
          v-if="isSoldOut"
          :deal="deal"
        />
      </div>
      <ProductsFounders
        v-if="hasFoundersPost"
        class="pdp-section"
        :product-url="deal.product_url"
        :is-select="!deal.is_marketplace"
        :founders="deal.founders"
      />
      <div class="container">
        <SmartCollectionZone
          v-if="useSmartCollectionZones"
          zone="pdp_before_questions"
          :template-slug="deal.slug"
          :relative-to-container="true"
          :conditions="zoneConditions"
          :preloaded-data="zoneData.pdp_before_questions"
        />
        <div class="row">
          <div class="col-lg-7 col-xl-8">
            <PDPDiscussionTabs
              :deal="deal"
              :user="user"
              :filter-comments="filterComments"
            />
          </div>
        </div>
        <SmartCollectionZone
          v-if="useSmartCollectionZones"
          zone="pdp_after_questions"
          :template-slug="deal.slug"
          :relative-to-container="true"
          :conditions="zoneConditions"
          :preloaded-data="zoneData.pdp_after_questions"
        />
      </div>

      <!-- if taxonomy, show breadcrumbs -->
      <Breadcrumbs
        v-if="showBreadcrumbs"
        class="container d-lg-none"
        :class="{ 'mb-20' : !user.isAuthenticated }"
        :deal-name="deal.public_name"
        :group="breadcrumbObjects.find((c) => c.slug === 'group')"
        :category="breadcrumbObjects.find((c) => c.slug === 'category')"
        :subcategory="breadcrumbObjects.find((c) => c.slug === 'subcategory')"
      />

      <SignUpBanner v-if="!user.isAuthenticated" />
    </div>
  </div>
</template>

<script>
import mapKeys from 'lodash/mapKeys';
import get from 'lodash/get';
import catchify from 'catchify';
import dayjs from 'dayjs';
import { getCollectionZones } from '~/datalayer/helpers/collections';
import PDPDealStory from '../../../components/pdp/PDPDealStory';
import PDPSectionTabs from '../../../components/pdp/PDPSectionTabs';
import PDPDiscussionTabs from '../../../components/pdp/discussion/PDPDiscussionTabs';
import { getProductPageLDSchemaJSONString, getTaxonomyBreadcrumbLDSchemaJSONString, getVideoObjectLDSchemaJSONString } from '../../../util/schemaLD';
import { getPdpData } from '../../../util/get-pdp-data';
import { DiscussionType } from '../../../constants/discussions';
import {
  ACTIVITY_TYPE, PDP_BREADCRUMB_SLUGS, SUMO_DAY_BANNER_TEXT,
  BLACK_FRIDAY_BANNER_TEXT, SC_ZONE_EXPERIMENT_NAME,
  MOBILE_STICKY_COUNTDOWN_TIMER_EXPERIMENT_NAME,
  PDP_ASKSUMO_EXPERIMENT_NAME,
  DESKTOP_STICKY_COUNTDOWN_TIMER_EXPERIMENT_NAME,
  PDP_DESKTOP_TIMER_EXPERIMENT_NAME,
  HIDE_STICKY_TABS_EXPERIMENT_NAME,
  GA4_MIGRATION,
} from '../../../constants/global';
import { DEAL_STAGE } from '~/constants/deal-stages';
import PrivyMixin from '~/mixins/add-privy';
import Loader from '../../../components/common/Loader.vue';
import SmartCollectionZone from '~/components/smartcollections/SmartCollectionZone';
import Breadcrumbs from '~/components/pdp/Breadcrumbs';
import { getZoneConditions } from '~/util/collections';

const DISCUSSION_PERMALINK_REGEX = /^#([rq])([0-9]*)$/;

export default {
  components: {
    Loader,
    PDPDealStory,
    PDPSectionTabs,
    PDPDiscussionTabs,
    SmartCollectionZone,
    Breadcrumbs,
    // dynamic import based on condition (separate chunk per file)
    DealBannerHeader: () => import('../../../components/pdp/DealBannerHeader.vue'),
    SignUpBanner: () => import('../../../components/common/SignUpBanner.vue'),
    DealTermsAndFeatures: () => import('~/components/products/terms-and-features/DealTermsAndFeatures.vue'),
    PricingTable: () => import('~/components/products/pricing-table/PricingTable.vue'),
    ProductsFounders: () => import('~/components/products/founders/ProductsFounders.vue'),
  },
  mixins: [PrivyMixin],
  provide() {
    // to ease the use for all descendents let's provide the deal, story, and user to all
    // ~ descendents so that we do not need to thread these down through the stack.
    // note: this was added WAY after the pdp page had been built, so not used as much as it could.
    const { deal, story, user } = this;

    return { deal, story, user };
  },
  layout: 'base',
  DiscussionType,
  async asyncData(context) {
    const data = await getPdpData(context);

    let zoneData = {};
    const {
      $cachios, route, store,
    } = context;

    // if we failed to load the details for the deal and user from the server then redirect to
    // ~ browse and hopefully they will have more luck there.
    // JIC, should already be redirected by redirect middleware
    if (!data) {
      // note: Should we do something better here. 404 or toast, etc?
      return context.redirect('/');
    }

    if (data.deal?.banner_details?.text === 'Ending soon') {
      await Promise.all([
        store.dispatch('experiments/enrollByName', MOBILE_STICKY_COUNTDOWN_TIMER_EXPERIMENT_NAME),
        store.dispatch('experiments/enrollByName', DESKTOP_STICKY_COUNTDOWN_TIMER_EXPERIMENT_NAME),
        store.dispatch('experiments/enrollByName', PDP_DESKTOP_TIMER_EXPERIMENT_NAME),
      ]);
    }

    // run now so we avoid any layout shift and have the data at time of render
    await Promise.all([
      data.deal?.buy_button?.is_ended === true ? store.dispatch('experiments/enrollByName', SC_ZONE_EXPERIMENT_NAME) : Promise.resolve(),
      !data.user?.isAuthenticated ? store.dispatch('experiments/enrollByName', PDP_ASKSUMO_EXPERIMENT_NAME) : Promise.resolve(),
    ]);

    const useSmartcollectionZones = (
      store.state.experiments[SC_ZONE_EXPERIMENT_NAME] === 'enabled'
      && data.deal?.buy_button?.is_ended === true
    );

    if (useSmartcollectionZones) {
      const { dealSlug } = route.params;
      const conditions = getZoneConditions(data.deal);
      zoneData = await getCollectionZones(
        $cachios,
        ['pdp_top', 'pdp_before_terms', 'pdp_before_questions', 'pdp_after_questions'],
        conditions,
        dealSlug,
      );
    }

    const filterComments = context.$config.allowFilterComments === 'enabled' || false;

    return {
      // the deal, story and user will all come from the data.
      ...data,
      zoneData,
      forceRedirectOnClientSide: false,
      filterComments,
    };
  },
  head() {
    const { deal, story } = this;

    if (!deal || !story) {
      return { title: 'AppSumo' };
    }

    let title = deal.public_name;

    if (story.meta_title) {
      title += ` - ${story.meta_title} | AppSumo`;
    } else {
      title += ' | AppSumo';
    }

    const optionalScripts = [];

    if (deal.top_5_reviews.length
      && deal.deal_review.review_count
      && deal.deal_review.average_rating) {
      // we tried using a skip: for this, but we were getting null pointer issues because
      // ~ getProductPageLDSchemaJSONString() was still being called
      optionalScripts.push({
        hid: 'pdp-schema',
        type: 'application/ld+json',
        innerHTML: getProductPageLDSchemaJSONString(deal),
      });
    }

    if (this.group && this.deal) {
      optionalScripts.push({
        hid: 'bc-schema',
        type: 'application/ld+json',
        innerHTML: getTaxonomyBreadcrumbLDSchemaJSONString({
          group: this.group,
          category: this.category,
          subcategory: this.subcategory,
          deal: this.deal,
        }),
      });
    }

    if (this.story.videos?.length) {
      const videoSchema = getVideoObjectLDSchemaJSONString(this.deal);

      if (videoSchema) {
        optionalScripts.push({
          hid: 'video-schema',
          type: 'application/ld+json',
          innerHTML: getVideoObjectLDSchemaJSONString(this.deal),
        });
      }
    }

    const bodyAttrs = {};

    bodyAttrs.class = ['pdp-page']; // merge with base.vue classes

    return {
      title,
      bodyAttrs,
      meta: [
        {
          hid: 'og:title',
          property: 'og:title',
          content: title,
        },
        {
          hid: 'description',
          property: 'description',
          content: story.card_description,
          skip: !story.card_description,
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: story.card_description,
          skip: !story.card_description,
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: deal.media_url,
          skip: !deal.media_url,
        },
        {
          hid: 'og:type',
          property: 'og:type',
          content: 'article',
        },
        {
          hid: 'twitter:title',
          property: 'twitter:title',
          content: title,
        },
        {
          hid: 'twitter:description',
          property: 'twitter:description',
          content: story.card_description,
          skip: !story.card_description,
        },
        {
          hid: 'twitter:image',
          property: 'twitter:image',
          content: deal.media_url,
          skip: !deal.media_url,
        },
      ],
      link: [
        {
          hid: 'canonical',
          rel: 'canonical',
          href: `https://appsumo.com${deal.get_absolute_url}`,
        },
      ],
      script: [
        {
          async: true,
          src: `https://www.googletagmanager.com/gtag/js?id=${this.gaTrackingId}`,
          skip: !this.gaTrackingId,
        },
        ...optionalScripts,
      ],
      // avoid encoding (&quot; instead ")
      __dangerouslyDisableSanitizersByTagID: {
        'pdp-schema': ['innerHTML'],
        'bc-schema': ['innerHTML'],
        'video-schema': ['innerHTML'],
      },
    };
  },
  computed: {
    isGa4Enabled() {
      return this.$store.getters['experiments/getExperimentVariant'](GA4_MIGRATION) === 'enabled';
    },
    hideStickyTabs() {
      return this.$store.getters['experiments/getExperimentVariant'](
        HIDE_STICKY_TABS_EXPERIMENT_NAME,
      ) === 'hide';
    },
    zoneConditions() {
      return getZoneConditions(this.deal);
    },
    useSmartCollectionZones() {
      return this.$store.state.experiments[SC_ZONE_EXPERIMENT_NAME] === 'enabled' && this.isSoldOut;
    },
    group() {
      const group = this.breadcrumbObjects.find((c) => c.slug === 'group');

      if (group && group.values.length > 0) {
        return mapKeys(group.values[0], (v, k) => k.replace('slug', 'value'));
      }

      return null;
    },
    category() {
      const category = this.breadcrumbObjects.find((c) => c.slug === 'category');

      if (category && category.values.length > 0) {
        return mapKeys(category.values[0], (v, k) => k.replace('slug', 'value'));
      }

      return null;
    },
    subcategory() {
      const subcategory = this.breadcrumbObjects.find((c) => c.slug === 'subcategory');

      if (subcategory && subcategory.values.length > 0) {
        return mapKeys(subcategory.values[0], (v, k) => k.replace('slug', 'value'));
      }

      return null;
    },
    productTag() {
      const productTag = this.deal.attributes.find((c) => c.slug === 'product_tag');

      if (productTag && productTag.values.length > 0) {
        return mapKeys(productTag.values[0], (v, k) => k.replace('slug', 'value'));
      }

      return null;
    },
    breadcrumbObjects() {
      return this.deal?.attributes?.filter((a) => PDP_BREADCRUMB_SLUGS.includes(a.slug));
    },
    showBreadcrumbs() {
      // only for staff initially
      return this.breadcrumbObjects.length === 3;
    },
    isSoldOut() {
      // notes: info deals will show as 'ended'
      //       'sold out'/is_ended is a subset of is_inactive_indefinitely
      return this.deal.buy_button.is_ended;
    },
    gaTrackingId() {
      if (!get(this, 'deal.partner_ga_tags')) {
        return null;
      }

      // partner_ga_tags is an array of GA tracking IDs, in the future we might need to store
      // multiple tags for the same deal, for now we just need the most recent one.
      const gaTags = [...this.deal.partner_ga_tags].reverse();
      return gaTags[0];
    },
    linkHash() {
      return this.$route?.hash || '';
    },
    inactiveIndefinitely() {
      return this.deal.stage_details.is_inactive_indefinitely;
    },
    hasFoundersPost() {
      return !!this.deal.founders?.posts?.length;
    },
    currentlyBeforeNextDealStage() {
      const nextStageStartDate = this.deal?.stage_details?.next_stage_start_date;
      if (!nextStageStartDate) {
        return false;
      }

      const dayjsNow = dayjs();
      const diffInMilliseconds = dayjs(nextStageStartDate).diff(dayjsNow);

      return diffInMilliseconds > 0;
    },
    lowCodesRemaining() {
      return this.deal.shows_codes_remaining && this.deal.codes_remaining > 0;
    },
    showBannerDetails() {
      if (!this.deal.banner_details?.text) {
        return false;
      }

      // hide traditional banner if 'ending soon' & under experiment
      if (
        this.$store.state.experiments[
          DESKTOP_STICKY_COUNTDOWN_TIMER_EXPERIMENT_NAME
        ] === 'enabled'
        || this.$store.state.experiments[
          PDP_DESKTOP_TIMER_EXPERIMENT_NAME
        ] === 'enabled'
      ) {
        return false;
      }

      return this.currentlyBeforeNextDealStage
          || this.lowCodesRemaining
          || this.deal.banner_details?.text === SUMO_DAY_BANNER_TEXT
          || this.deal.banner_details?.text === BLACK_FRIDAY_BANNER_TEXT;
    },
    displayTimer() {
      return this.deal.active_timer
          && this.deal.stage_details?.current_stage !== DEAL_STAGE.INACTIVE
          && this.currentlyBeforeNextDealStage;
    },
  },
  async created() {
    // Update cookies, needed as server can't update from asyncData; this is
    // used for routing to either nuxt or django in nginx
    if (this.deal.render_nuxt_pdp.nuxt) {
      this.$cookies.set('pdp_type', 'nuxt');
    } else {
      this.$cookies.set('pdp_type', 'django');
    }

    // If the deal and user settings specify, we will redirect to Django
    if (!this.deal.render_nuxt_pdp.nuxt) {
      this.forceRedirectOnClientSide = true;
    } else if (this.isGa4Enabled) {
      this.setEcommerceDataLayerGA4();
    } else {
      this.setEcommerceDataLayer();
    }

    if (typeof window === 'undefined') {
      return;
    }

    if (this.forceRedirectOnClientSide) {
      // ensure we keep all params except rm, which we will be forcing to d below
      const queryParams = Object.entries(this.$route.query)
        .filter(([k]) => k !== 'rm')
        .map(([k, v]) => `${k}=${v}`)
        .join('&');

      let redirectUrl = `${this.$route.path}`;
      if (queryParams) {
        redirectUrl += `?${queryParams}`;
      }
      redirectUrl += this.$route.hash;

      // eslint-disable-next-line no-restricted-globals
      location.href = redirectUrl;

      return;
    }

    // unfortunately the URL fragment is not available in asyncData, so we are forced to do this
    // ~ discussion permalink checking here in created.
    if (this.deal.render_nuxt_pdp.support_discussion_pages && this.$route?.hash) {
      // use a regex to validate and extract what we care about from the URL fragment
      const discussionFragment = this.$route.hash.match(DISCUSSION_PERMALINK_REGEX);
      if (discussionFragment) {
        const pathType = discussionFragment[1] === 'r'
          ? 'reviews'
          : 'questions';

        // Since we are redirecting the user let's show a spinner here.
        this.forceRedirectOnClientSide = true;

        // We are burying discussion id in the "slug" with a -{id} suffix, but the
        // ~ prefix slug part is entirely optional, so we can just specify the provided
        // ~ id and call it a day. See discussion-page-mixin.js DISCUSSION_SLUG_REGEX
        // eslint-disable-next-line no-restricted-globals
        location.href = `${this.deal.get_absolute_url}${pathType}/${discussionFragment[2]}/`;
        return;
      }
    }

    // If staying on PDP log activity
    this.postPageActivity();
  },
  mounted() {
    if (this.gaTrackingId) {
      // If partner wants to track, let's register their google analytics ID
      // note: This config immediately starts the tracking process.
      this.gtag('js', new Date());
      this.gtag('config', this.gaTrackingId); // 'send_page_view' defaults to true
    }
  },
  methods: {
    gtag(...args) {
      // https://developers.google.com/tag-platform/gtagjs/install#google-analytics
      window.dataLayer = window.dataLayer || [];
      dataLayer.push(args);
    },
    async postPageActivity() {
      const { $axios, $cookies, deal } = this;

      // User must be authenticated to register activity, but not views
      const options = {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': $cookies.get('csrftoken'),
        },
      };

      const data = { activity_event: ACTIVITY_TYPE.DEAL_VIEW };

      await catchify($axios({
        method: 'post',
        url: `/deal/${deal.id}/activity/`,
        data,
        ...options,
      }));
    },
    setEcommerceDataLayerGA4() {
      if (process.server) {
        return;
      }

      // GA4 item object for view_item event
      const item = {
        item_name: this.deal.public_name,
        item_variant: this.deal.default_plan.tier,
        item_id: this.deal.id.toString() || '',
        price: this.deal.default_price || 0,
        affiliation: this.deal.products?.[0]?.partner || '',
        item_brand: this.deal.listing_type || '',
        item_category: this.group?.value || '',
        item_category2: this.category?.value || '',
        item_category3: this.subcategory?.value || '',
        item_category4: this.productTag?.value || '',

        // Custom parameters
        deal_status: this.deal.deal_status.toLowerCase(),
        subheader: this.deal.products?.[0]?.story?.subheader || '',
        url: `https://appsumo.com${this.deal.get_absolute_url}`,
        endDate: this.deal.dates?.end_date ? dayjs(this.deal.dates?.end_date).format('MM/DD/YYYY hh:mm A') : '',
        comparisonPrice: this.deal.original_price.toString() || '',
        averageRating: this.deal.deal_review?.average_rating || '',
        topReview: this.deal.top_5_reviews?.[0]?.comment || '',
        image: this.deal.media_url ? `${this.deal.media_url}?width=250&height=250` : '',
      };

      // TODO: Remove this once we complete the GA4 migration
      // Content group for UA.
      this.$gtag('set', {
        contentGroup1: this.deal.listing_type,
        contentGroup2: this.deal.deal_status.toLowerCase(),
      });

      this.$gtag('event', 'view_item', {
        // TODO: Remove this once we complete the GA4 migration
        // Content groups for UA only, GA4 will use `item_brand` and `deal_status`
        content_group: this.deal.listing_type,
        content_group_2: this.deal.deal_status.toLowerCase(),
        currency: 'USD',

        // Value of the item from a CVR perspective
        value: this.deal.default_price || 0,
        items: [item],
      });
    },
    setEcommerceDataLayer() {
      const product = {
        name: this.deal.public_name,
        category: this.deal.is_marketplace ? 'marketplace' : 'select',
        subheader: this.deal.products?.[0]?.story?.subheader || '',
        sku: this.deal.id.toString() || '',
        price: this.deal.default_price.toString() || '',
        url: `https://appsumo.com${this.deal.get_absolute_url}`,
        endDate: this.deal.dates?.end_date ? dayjs(this.deal.dates?.end_date).format('MM/DD/YYYY hh:mm A') : '',
        comparisonPrice: this.deal.original_price.toString() || '',
        averageRating: this.deal.deal_review?.average_rating || '',
        topReview: this.deal.top_5_reviews?.[0]?.comment || '',
      };

      if (this.deal.media_url) {
        product.image = `${this.deal.media_url}?width=250&height=250`;
      }

      // we want to include information about the detail as its own layer
      this.$gtm.push({
        contentGroup1: this.deal.listing_type,
        contentGroup2: this.deal.deal_status.toLowerCase(),
      });

      this.$gtm.push({
        event: 'productDetails',
        ecommerce: {
          detail: {
            products: [product],
          },
        },
      });
    },
  },
};
</script>

<style lang="scss">
  @import '~/styles/utils/pdp-main-container';
  @import '~/styles/utils/breakpoints';

  .pdp-section {
    margin-top: 24px;
    margin-bottom: 40px;
  }

  /* let default text size be 16px */
  p,
  .fs-16 {
    font-size: 16px;
  }

  iframe {
    width: 100%;
    height: 100%;
    aspect-ratio: 16/9;
  }

  @include media-breakpoint-down(md) {
    /* AB PDP Mobile Above the Fold */
    .pdp-page .pdp-section {
      margin-top: 0;
    }
  }

  @media (min-width: 1024px) {
    .pdp-section {
      margin-bottom: 64px;
    }
  }
</style>
